// Route Registry - Ensures link integrity across the application
// 29 Supported Languages (Global BPO Strategy)
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'ar', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'ko', 'th', 'tr', 'vi', 'el', 'sv', 'da', 'fi', 'no', 'hu', 'cs', 'ro', 'id', 'ms', 'tl', 'bn', 'hi'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const TIER1_LANGUAGES: SupportedLanguage[] = ['en', 'fr', 'es', 'ar', 'de'];
export const TIER2_LANGUAGES: SupportedLanguage[] = ['it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'ko', 'th', 'tr', 'vi', 'el', 'sv', 'da', 'fi', 'no', 'hu', 'cs', 'ro', 'id', 'ms', 'tl', 'bn', 'hi'];

export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

export const isRTL = (lang: SupportedLanguage): boolean => RTL_LANGUAGES.includes(lang);
export const isTier1 = (lang: SupportedLanguage): boolean => TIER1_LANGUAGES.includes(lang);

// Route definitions
export const routes = {
  home: '',
  services: 'services',
  servicesBpo: 'services/bpo',
  servicesBpoInbound: 'services/bpo/inbound-customer-care',
  servicesBpoTechnical: 'services/bpo/technical-support',
  servicesBpoOutbound: 'services/bpo/outbound-sales',
  servicesBpoRetention: 'services/bpo/retention-winback',
  servicesBpoSdr: 'services/bpo/sdr-appointment-setting',
  servicesBpoResearch: 'services/bpo/market-research-surveys',
  servicesBpoDigital: 'services/bpo/digital-engagement',
  servicesBpoTrust: 'services/bpo/trust-safety-compliance',
  servicesBpoAiOps: 'services/bpo/ai-ops-backoffice',
  servicesBpoMultilingual: 'services/bpo/multilingual-support',
  servicesDigital: 'services/digital-studio',
  servicesDigitalSaas: 'services/digital-studio/custom-saas',
  servicesDigitalPortals: 'services/digital-studio/enterprise-portals',
  servicesDigitalEcommerce: 'services/digital-studio/ecommerce',
  servicesDigitalApi: 'services/digital-studio/api-integration',
  servicesDigitalMobile: 'services/digital-studio/mobile-apps',
  servicesDigitalUiux: 'services/digital-studio/ui-ux-design',
  servicesDigitalCloud: 'services/digital-studio/cloud-devops',
  servicesDigitalGrowth: 'services/digital-studio/growth-marketing',
  servicesDigitalAi: 'services/digital-studio/emerging-tech-ai',
  servicesDigitalTechStack: 'services/digital-studio/tech-stack-showcase',
  servicesDigitalSynergy: 'services/digital-studio/synergy',
  servicesDigitalAiMl: 'services/digital-studio/ai-ml-development',
  servicesDigitalBlockchain: 'services/digital-studio/blockchain-web3',
  servicesDigitalIot: 'services/digital-studio/iot-development',
  servicesDigitalLegacy: 'services/digital-studio/legacy-modernization',
  servicesDigitalStaffAug: 'services/digital-studio/staff-augmentation',
  servicesDigitalArVr: 'services/digital-studio/ar-vr-development',
  servicesBpoFinance: 'services/bpo/finance-accounting',
  servicesBpoHrPayroll: 'services/bpo/hr-payroll',
  servicesBpoHealthcare: 'services/bpo/healthcare-bpo',
  servicesBpoVirtualAssistant: 'services/bpo/virtual-assistant',
  servicesBpoLogistics: 'services/bpo/logistics-supply-chain',
  servicesBpoRecruitment: 'services/bpo/recruitment-staffing',
  servicesBpoCollections: 'services/bpo/collections-payment',
  servicesBpoOrderMgmt: 'services/bpo/order-management',
  servicesBpoLiveChat: 'services/bpo/live-chat-messaging',
  servicesBpoSocialMedia: 'services/bpo/social-media-care',
  servicesBpo247Support: 'services/bpo/24-7-support',
  servicesBpoEmailSupport: 'services/bpo/email-support',
  servicesBpoCustomerSuccess: 'services/bpo/customer-success-saas',
  servicesBpoQaCoaching: 'services/bpo/qa-coaching',
  servicesBpoBilling: 'services/bpo/billing-collections',
  servicesBpoCustomerRetention: 'services/bpo/customer-retention',
  servicesBpoLeadQualification: 'services/bpo/lead-qualification',
  servicesBpoFraudDetection: 'services/bpo/fraud-detection',
  servicesBpoComplianceMonitoring: 'services/bpo/compliance-monitoring',
  servicesBpoContentReview: 'services/bpo/content-review',
  servicesBpoDataEntry: 'services/bpo/data-entry-processing',
  // Digital Studio additions
  servicesDigitalPenTest: 'services/digital-studio/penetration-testing',
  servicesDigitalSoc: 'services/digital-studio/soc-as-a-service',
  servicesDigitalMobileDev: 'services/digital-studio/mobile-development',
  servicesDigitalWebDev: 'services/digital-studio/web-development',
  // Solutions
  solutions: 'solutions',
  solutionsCx: 'solutions/customer-experience-augmented',
  solutionsBpo: 'solutions/bpo-contact-center',
  solutionsDigital: 'solutions/digital-factory',
  solutionsTrust: 'solutions/trust-and-safety',
  solutionsBestInClass: 'solutions/best-in-class-apps',
  solutionsArchitecture: 'solutions/reference-architecture',
  solutionsRouting: 'solutions/routing-playbook',
  industries: 'industries',
  caseStudies: 'case-studies',
  caseStudySaas: 'case-studies/saas-support-scale',
  caseStudyEcommerce: 'case-studies/ecommerce-conversion-chat',
  caseStudyFintech: 'case-studies/fintech-kyc-ops',
  pricing: 'pricing',
  checkoutSuccess: 'checkout/success',
  checkoutCancel: 'checkout/cancel',
  about: 'about',
  expertise: 'expertise',
  careers: 'careers',
  insights: 'blog',
  contact: 'contact',
  resourcesDeck: 'resources/deck',
  legalPrivacy: 'legal/privacy',
  legalTerms: 'legal/terms',
  legalCookies: 'legal/cookies',
  // V49 Utility Pages
  quote: 'quote',
  booking: 'booking',
  whistleblowing: 'whistleblowing',
  portal: 'portal',
  jobs: 'jobs',
  notFound: '404',
} as const;

export type RouteKey = keyof typeof routes;

// Helper to generate localized path
export const getLocalizedPath = (lang: SupportedLanguage, routeKey: RouteKey): string => {
  const route = routes[routeKey];
  return `/${lang}${route ? `/${route}` : ''}`;
};

// Helper to generate path with current language
export const createPathHelper = (lang: SupportedLanguage) => {
  return (routeKey: RouteKey): string => getLocalizedPath(lang, routeKey);
};

// Validate that a route exists
export const isValidRoute = (path: string): boolean => {
  const routeValues = Object.values(routes);
  return routeValues.some(route => path.endsWith(route) || path === route);
};

// Get language from path
export const getLanguageFromPath = (pathname: string): SupportedLanguage => {
  const segments = pathname.split('/').filter(Boolean);
  const lang = segments[0] as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : 'en';
};

// Get route without language prefix
export const getRouteWithoutLang = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  if (SUPPORTED_LANGUAGES.includes(segments[0] as SupportedLanguage)) {
    return '/' + segments.slice(1).join('/');
  }
  return pathname;
};

// Navigation items for header
export const getNavItems = (lang: SupportedLanguage) => {
  const path = createPathHelper(lang);
  return {
    main: [
      {
        key: 'services',
        href: path('services'),
        megaMenu: {
          bpo: {
            title: 'bpoSolutions',
            href: path('servicesBpo'),
            items: [
              { key: 'inbound', href: path('servicesBpoInbound') },
              { key: 'technical', href: path('servicesBpoTechnical') },
              { key: 'outbound', href: path('servicesBpoOutbound') },
              { key: 'retention', href: path('servicesBpoRetention') },
              { key: 'sdr', href: path('servicesBpoSdr') },
              { key: 'digital', href: path('servicesBpoDigital') },
              { key: 'trust', href: path('servicesBpoTrust') },
              { key: 'finance', href: path('servicesBpoFinance') },
              { key: 'healthcare', href: path('servicesBpoHealthcare') },
              { key: 'virtualAssistant', href: path('servicesBpoVirtualAssistant') },
            ],
          },
          digital: {
            title: 'digitalFactory',
            href: path('servicesDigital'),
            items: [
              { key: 'saas', href: path('servicesDigitalSaas') },
              { key: 'portals', href: path('servicesDigitalPortals') },
              { key: 'ecommerce', href: path('servicesDigitalEcommerce') },
              { key: 'mobile', href: path('servicesDigitalMobile') },
              { key: 'uiux', href: path('servicesDigitalUiux') },
              { key: 'cloud', href: path('servicesDigitalCloud') },
              { key: 'ai', href: path('servicesDigitalAi') },
              { key: 'aiMl', href: path('servicesDigitalAiMl') },
              { key: 'arVr', href: path('servicesDigitalArVr') },
              { key: 'staffAug', href: path('servicesDigitalStaffAug') },
            ],
          },
        },
      },
      { key: 'industries', href: path('industries') },
      { key: 'caseStudies', href: path('caseStudies') },
      { key: 'pricing', href: path('pricing') },
      { key: 'insights', href: path('insights') },
      { key: 'careers', href: path('careers') },
      { key: 'jobs', href: path('jobs') },
      { key: 'contact', href: path('contact') },
    ],
    cta: {
      primary: { key: 'getQuote', href: path('contact') },
      secondary: { key: 'buyServices', href: path('pricing') },
    },
  };
};

// Blog/Insights posts
export const insightsSlugs = [
  'ai-transforming-customer-service',
  'multilingual-support-best-practices',
  'outsourcing-vs-inhouse-2024',
  'contact-center-automation',
  'crm-integration-strategies',
  'data-security-compliance',
  'customer-experience-metrics',
  'scaling-support-operations',
  'ai-call-center-revolution-2026',
  'nearshore-vs-offshore-bpo-comparison-2026',
  'multilingual-customer-support-strategy-guide',
  'saas-customer-success-outsourcing-playbook',
  'contact-center-kpis-benchmarks-2026',
  'ecommerce-customer-service-outsourcing',
  'healthcare-bpo-hipaa-compliance-guide',
  'fintech-bpo-gdpr-kyc-compliance',
  'virtual-assistant-outsourcing-guide-2026',
  'finance-accounting-bpo-cost-savings',
  'staff-augmentation-vs-outsourcing-guide',
  'blockchain-web3-development-enterprise-guide',
  'iot-development-smart-solutions-2026',
  'legacy-modernization-migration-strategy',
  'ar-vr-enterprise-training-roi',
  'ai-ml-development-build-vs-buy-2026',
] as const;

export type InsightSlug = typeof insightsSlugs[number];
